import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import PageHead from "@/components/PageHead";

/**
 * Public unsubscribe page. Reads the `token` query param, verifies it against
 * the backend, shows the masked email, and lets the visitor confirm the
 * unsubscription with one click. Never leaks the raw email.
 */
export default function Unsubscribe() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const campaignId = params.get("c") || "";
    const [state, setState] = useState({ status: "loading", email: "", already: false, error: "" });
    const [confirming, setConfirming] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            setState({ status: "error", error: "Link inválido — falta o token." });
            return;
        }
        api.get(`/newsletter/unsubscribe/verify`, { params: { token } })
            .then((r) => setState({ status: "ok", email: r.data.email_masked, already: r.data.already_unsubscribed, error: "" }))
            .catch((err) => setState({ status: "error", email: "", already: false, error: err?.response?.data?.detail || "Link inválido ou expirado." }));
    }, [token]);

    const confirm = async () => {
        setConfirming(true);
        try {
            // `campaign_id` is optional — passed for analytics attribution only.
            // Missing/invalid values are silently ignored by the backend.
            await api.post(`/newsletter/unsubscribe`, campaignId ? { token, campaign_id: campaignId } : { token });
            setDone(true);
        } catch (err) {
            setState((s) => ({ ...s, status: "error", error: err?.response?.data?.detail || "Não foi possível cancelar a subscrição." }));
        } finally { setConfirming(false); }
    };

    return (
        <>
            <PageHead
                title="Cancelar subscrição — O Homem da Obra"
                description="Cancelar subscrição da newsletter O Homem da Obra."
                canonical="https://ohomemdaobra.pt/unsubscribe"
            />
            <section className="min-h-[70vh] py-20 flex items-center">
                <div className="max-w-lg mx-auto px-6 sm:px-10 text-center">
                    {state.status === "loading" && (
                        <>
                            <Loader2 className="w-8 h-8 text-stone-400 animate-spin mx-auto mb-4" />
                            <p className="text-stone-500">A validar o seu pedido…</p>
                        </>
                    )}

                    {state.status === "error" && (
                        <>
                            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-6" />
                            <h1 className="font-display text-3xl font-bold text-stone-900 mb-3">Link inválido</h1>
                            <p className="text-stone-600 mb-8">{state.error}</p>
                            <Link to="/" className="btn-copper">Voltar ao site<span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span></Link>
                        </>
                    )}

                    {state.status === "ok" && !done && !state.already && (
                        <>
                            <div className="eyebrow mb-6 justify-center inline-flex"><span className="logo-mark text-sm text-white">H<span className="dot !text-[hsl(var(--copper))]">.</span></span> Newsletter</div>
                            <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-[1.05]">
                                Tem a certeza que quer <span className="font-serif-italic text-[hsl(var(--copper))]">cancelar</span>?
                            </h1>
                            <p className="text-stone-600 mt-4 mb-8">
                                Vamos remover <b>{state.email}</b> da lista da newsletter O Homem da Obra. Não voltará a receber os nossos emails.
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                <button onClick={confirm} disabled={confirming} className="btn-copper disabled:opacity-50" data-testid="unsub-confirm">
                                    {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sim, cancelar subscrição"}
                                </button>
                                <Link to="/" className="btn-outline-dark">
                                    Manter subscrição
                                    <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                                </Link>
                            </div>
                        </>
                    )}

                    {state.status === "ok" && state.already && !done && (
                        <>
                            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-6" />
                            <h1 className="font-display text-3xl font-bold text-stone-900 mb-3">Subscrição já cancelada</h1>
                            <p className="text-stone-600 mb-8">O email <b>{state.email}</b> já não está inscrito na newsletter.</p>
                            <Link to="/" className="btn-copper">Voltar ao site<span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span></Link>
                        </>
                    )}

                    {done && (
                        <>
                            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-6" data-testid="unsub-done" />
                            <h1 className="font-display text-3xl font-bold text-stone-900 mb-3">Subscrição cancelada</h1>
                            <p className="text-stone-600 mb-8">Pronto — <b>{state.email}</b> foi removido da nossa lista. Se mudar de ideias, pode voltar a subscrever a partir do site.</p>
                            <Link to="/" className="btn-copper">Voltar ao site<span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span></Link>
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
